import axios from "axios";

import { reportingConstants as C } from "../constants";

export const reportingActions = {
  getReportingWidgets,
  createReportingWidget,
  updateReportingWidget,
  deleteReportingWidget,
  createReportingSeries,
  createReportingSeriesBulk,
  updateReportingSeries,
  deleteReportingSeries,
  reorderReportingSeries,
  checkReportingIntegrity,
  setReportingIntegrityFalse,
};

const EMPTY_SERIES_FIELDS = {
  business_case_id: -1,
  scenario_id: -1,
  account_id: -1,
  associated_account_id: -1,
  tab_name: "",
  account_number: "",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// axios throws on any non-2xx status, so errors are handled in catch only.
function errorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data?.message) return data.message;
  if (data?.error) return String(data.error);
  return error?.message || fallback;
}

// Optimistic updates can race: if a row is edited twice quickly, the first
// response must not overwrite the second edit. Each request gets a token and
// only the latest one for a given row is allowed to apply its result.
const latestToken = new Map();
const nextToken = (key) => {
  const token = (latestToken.get(key) ?? 0) + 1;
  latestToken.set(key, token);
  return token;
};
const isLatest = (key, token) => latestToken.get(key) === token;

const findWidget = (state, widgetId) =>
  (state.reporting?.widgets || []).find((w) => w.widget_id === widgetId) ?? null;

const findSeries = (state, seriesId) => {
  for (const w of state.reporting?.widgets || []) {
    const index = (w.series || []).findIndex((s) => s.series_id === seriesId);
    if (index !== -1) return { series: w.series[index], index };
  }
  return null;
};

// ---------------------------------------------------------------------------
// Widgets
// ---------------------------------------------------------------------------
function getReportingWidgets({ force = false } = {}) {
  return async (dispatch, getState) => {
    const reporting = getState().reporting;

    // Don't start another fetch if one is in flight
    if (reporting?.fetchingWidgets) return;

    // Skip only if we already loaded successfully (unless forced)
    if (!force && reporting?.widgetsLoaded === true) return;

    dispatch({ type: C.GET_REPORTING_WIDGETS_REQUEST });

    try {
      const { data } = await axios.get("/reporting/widgets");
      dispatch({ type: C.GET_REPORTING_WIDGETS_SUCCESS, widgets: data });
    } catch (error) {
      dispatch({
        type: C.GET_REPORTING_WIDGETS_FAILURE,
        error: errorMessage(error, "Unable to load reporting. Please try again later."),
      });
    }
  };
}

function createReportingWidget(widget) {
  return async (dispatch) => {
    dispatch({ type: C.CREATE_REPORTING_WIDGET_REQUEST });

    try {
      const { data } = await axios.post("/reporting/widgets", { widget });
      dispatch({ type: C.CREATE_REPORTING_WIDGET_SUCCESS, widget: data });
      return data;
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.CREATE_REPORTING_WIDGET_FAILURE,
        error: errorMessage(error, "Unable to add the chart. Please try again later."),
      });
    }
  };
}

// Optimistic: the store updates immediately, the server response confirms it,
// and a failure restores the previous version.
function updateReportingWidget(widget) {
  return async (dispatch, getState) => {
    const key = `widget:${widget.widget_id}`;
    const token = nextToken(key);
    const previous = findWidget(getState(), widget.widget_id);

    dispatch({ type: C.UPDATE_REPORTING_WIDGET_REQUEST, widget });

    try {
      const { data } = await axios.put(`/reporting/widgets/${widget.widget_id}`, { widget });
      if (isLatest(key, token)) dispatch({ type: C.UPDATE_REPORTING_WIDGET_SUCCESS, widget: data });
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.UPDATE_REPORTING_WIDGET_FAILURE,
        error: errorMessage(error, "Unable to save the chart. Please try again later."),
        previous: isLatest(key, token) ? previous : null,
      });
    }
  };
}

function deleteReportingWidget(widgetId) {
  return async (dispatch, getState) => {
    const widgets = getState().reporting?.widgets || [];
    const index = widgets.findIndex((w) => w.widget_id === widgetId);
    const previous = index === -1 ? null : { widget: widgets[index], index };

    dispatch({ type: C.DELETE_REPORTING_WIDGET_REQUEST, widgetId });

    try {
      await axios.delete(`/reporting/widgets/${widgetId}`);
      dispatch({ type: C.DELETE_REPORTING_WIDGET_SUCCESS, widgetId });
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.DELETE_REPORTING_WIDGET_FAILURE,
        error: errorMessage(error, "Unable to remove the chart. Please try again later."),
        previous,
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Series
// ---------------------------------------------------------------------------
function createReportingSeries(series) {
  return async (dispatch) => {
    dispatch({ type: C.CREATE_REPORTING_SERIES_REQUEST });

    try {
      const { data } = await axios.post("/reporting/series", { series });
      dispatch({ type: C.CREATE_REPORTING_SERIES_SUCCESS, series: data });
      return data;
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.CREATE_REPORTING_SERIES_FAILURE,
        error: errorMessage(error, "Unable to add the series. Please try again later."),
      });
    }
  };
}

// Appends several series to one widget in a single request (all or nothing).
function createReportingSeriesBulk(widgetId, series) {
  return async (dispatch) => {
    dispatch({ type: C.CREATE_REPORTING_SERIES_BULK_REQUEST });

    try {
      const { data } = await axios.post("/reporting/series/bulk", { widget_id: widgetId, series });
      dispatch({ type: C.CREATE_REPORTING_SERIES_BULK_SUCCESS, widgetId, series: data });
      return data;
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.CREATE_REPORTING_SERIES_BULK_FAILURE,
        error: errorMessage(error, "Unable to add the accounts. Please try again later."),
      });
    }
  };
}

function updateReportingSeries(series) {
  return async (dispatch, getState) => {
    const key = `series:${series.series_id}`;
    const token = nextToken(key);
    const previous = findSeries(getState(), series.series_id)?.series ?? null;

    dispatch({ type: C.UPDATE_REPORTING_SERIES_REQUEST, series });

    try {
      const { data } = await axios.put(`/reporting/series/${series.series_id}`, { series });
      if (isLatest(key, token)) dispatch({ type: C.UPDATE_REPORTING_SERIES_SUCCESS, series: data });
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.UPDATE_REPORTING_SERIES_FAILURE,
        error: errorMessage(error, "Unable to save the series. Please try again later."),
        previous: isLatest(key, token) ? previous : null,
      });
    }
  };
}

function deleteReportingSeries(seriesId) {
  return async (dispatch, getState) => {
    const previous = findSeries(getState(), seriesId);

    dispatch({ type: C.DELETE_REPORTING_SERIES_REQUEST, seriesId });

    try {
      await axios.delete(`/reporting/series/${seriesId}`);
      dispatch({ type: C.DELETE_REPORTING_SERIES_SUCCESS, seriesId });
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.DELETE_REPORTING_SERIES_FAILURE,
        error: errorMessage(error, "Unable to remove the series. Please try again later."),
        previous,
      });
    }
  };
}

// Optimistic: orderedIds is the widget's complete series list in the new order.
function reorderReportingSeries(widgetId, orderedIds) {
  return async (dispatch, getState) => {
    const key = `series-order:${widgetId}`;
    const token = nextToken(key);
    const previous = findWidget(getState(), widgetId)?.series ?? null;

    dispatch({ type: C.REORDER_REPORTING_SERIES_REQUEST, widgetId, orderedIds });

    try {
      await axios.put(`/reporting/widgets/${widgetId}/series/order`, { ordered_ids: orderedIds });
      dispatch({ type: C.REORDER_REPORTING_SERIES_SUCCESS, widgetId });
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.REORDER_REPORTING_SERIES_FAILURE,
        error: errorMessage(error, "Unable to move the series. Please try again later."),
        widgetId,
        previous: isLatest(key, token) ? previous : null,
      });
    }
  };
}

// ---------------------------------------------------------------------------
// Integrity: clear references to business cases, scenarios, tabs and accounts
// that no longer exist.
//
// Pass only the lists you have loaded. A list that is left out is not
// checked, so a list that simply hasn't loaded yet can't wipe every series.
//
//   tabs:          array of tab names (strings) or objects with tab_name / name
//   allAccounts:   array of accounts with account_id
//   businessCases: array with business_case_id
//   allScenarios:  array with scenario_id
// ---------------------------------------------------------------------------
function checkReportingIntegrity({ tabs, allAccounts, businessCases, allScenarios } = {}) {
  return async (dispatch, getState) => {
    dispatch({ type: C.IS_REPORTING_INTEGRITY_INTACT_REQUEST });

    try {
      if (!getState().reporting?.widgetsLoaded) await dispatch(getReportingWidgets());
      const widgets = getState().reporting?.widgets || [];

      const idSet = (list, field) =>
        Array.isArray(list) ? new Set(list.map((x) => Number(x?.[field]))) : null;

      const tabNames = Array.isArray(tabs)
        ? new Set(tabs.map((t) => (typeof t === "string" ? t : t?.tab_name ?? t?.name)))
        : null;
      const bcIds = idSet(businessCases, "business_case_id");
      const scenarioIds = idSet(allScenarios, "scenario_id");
      const accountIds = idSet(allAccounts, "account_id");

      const noAccount = { account_id: -1, associated_account_id: -1, account_number: "" };
      const fixes = [];

      for (const widget of widgets) {
        for (const s of widget.series || []) {
          const bcGone = bcIds && s.business_case_id !== -1 && !bcIds.has(Number(s.business_case_id));
          const scenarioGone =
            scenarioIds && s.scenario_id !== -1 && !scenarioIds.has(Number(s.scenario_id));

          let next = null;
          if (bcGone || scenarioGone) {
            next = { ...s, ...EMPTY_SERIES_FIELDS };
          } else if (tabNames && s.tab_name !== "" && !tabNames.has(s.tab_name)) {
            next = { ...s, tab_name: "", ...noAccount };
          } else if (accountIds && s.account_id !== -1 && !accountIds.has(Number(s.account_id))) {
            next = { ...s, ...noAccount };
          }

          if (next) fixes.push(next);
        }
      }

      await Promise.all(fixes.map((s) => dispatch(updateReportingSeries(s))));
      dispatch({ type: C.IS_REPORTING_INTEGRITY_INTACT_SUCCES, reportingIntegrity: true });
    } catch (error) {
      console.error(error);
      dispatch({
        type: C.IS_REPORTING_INTEGRITY_INTACT_FAILURE,
        error: errorMessage(error, "Unable to check reporting at this moment. Please try again later."),
      });
    }
  };
}

function setReportingIntegrityFalse() {
  return (dispatch) => {
    dispatch({ type: C.SET_REPORTING_INTEGRITY_FALSE_SUCCESS, reportingIntegrity: false });
  };
}